import { Effect } from 'effect';
import { isAppHost } from '@/lib/host';

/**
 * Pure helpers for the PDF to Link runner (W.4.124): upload normalization,
 * the preview link shape, enum-only publish diagnostics and the framed-page
 * guard. Nothing here reads the network or React state, so the share kit and
 * the tool shell can import it too.
 */

/** Mirrors the server cap in host-api-routes.ts (HOST_GUEST_UPLOAD_MAX_BYTES). */
export const PDF_TO_LINK_MAX_BYTES = 5 * 1024 * 1024;

/** How the staged PDF reached the runner. Telemetry only. */
export type PdfToLinkSource = 'upload' | 'studio' | 'restored' | 'handoff';

export type PdfSizeBucket =
  | 'lt_100kb'
  | '100kb_500kb'
  | '500kb_1mb'
  | '1mb_2mb'
  | '2mb_5mb'
  | 'over_5mb';

const KIB = 1024;
const MIB = 1024 * KIB;

/** 1024-based size bucket; exactly 5 MiB (the accepted maximum) is still 2mb_5mb. */
export function sizeBucket(bytes: number): PdfSizeBucket {
  if (bytes < 100 * KIB) return 'lt_100kb';
  if (bytes < 500 * KIB) return '100kb_500kb';
  if (bytes < MIB) return '500kb_1mb';
  if (bytes < 2 * MIB) return '1mb_2mb';
  if (bytes <= PDF_TO_LINK_MAX_BYTES) return '2mb_5mb';
  return 'over_5mb';
}

const UPLOAD_NAME_MAX = 180;

function isControlChar(char: string): boolean {
  const code = char.charCodeAt(0);
  return code <= 0x1f || code === 0x7f;
}

/**
 * The leaf name the host API accepts: it requires a name ending in .pdf
 * (host-api-routes.ts readHostUploadForm). Studio documents and some OS
 * pickers hand over names without one.
 */
export function pdfUploadFileName(name: string): string {
  const leaf = name.slice(Math.max(name.lastIndexOf('/'), name.lastIndexOf('\\')) + 1);
  const cleaned =
    Array.from(leaf)
      .filter((char) => !isControlChar(char))
      .join('')
      .trim() || 'document';
  const withExt = /\.pdf$/i.test(cleaned) ? cleaned : `${cleaned}.pdf`;
  if (withExt.length <= UPLOAD_NAME_MAX) return withExt;
  // Keep the extension exactly as written; only the stem is cut.
  const ext = withExt.slice(-4);
  return `${withExt.slice(0, UPLOAD_NAME_MAX - ext.length)}${ext}`;
}

/**
 * Normalize the multipart file part after the magic-byte check: the server
 * takes a part type that is empty or exactly application/pdf, and the browser
 * reports application/x-pdf or '' for some real PDFs. A conforming File is
 * returned as the same instance, so no bytes are copied on the happy path.
 */
export const toUploadPdfFileEffect = Effect.fn('toolRunner.pdfToLink.normalizeUpload')(
  (file: File) =>
    Effect.sync(() => {
      const name = pdfUploadFileName(file.name);
      if (file.type === 'application/pdf' && name === file.name) return file;
      return new File([file], name, { type: 'application/pdf', lastModified: file.lastModified });
    }),
);

export function toUploadPdfFile(file: File): File {
  return Effect.runSync(toUploadPdfFileEffect(file));
}

/** The server reserves this label for custom-domain CNAME targets (custom-domain-hostname.ts). */
const RESERVED_NAMESPACE = 'cname';

/**
 * The link host the server will allocate, with the random suffix masked.
 * Mirrors hostedPdfNamespaceFromFileName (apps/api/server/src/hosted-pdf-index.ts)
 * applied to the name we upload: the namespace comes from the file name, not
 * the title.
 */
export function previewLinkHost(fileName: string): string {
  const slug = pdfUploadFileName(fileName)
    .replace(/\.pdf$/i, '')
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-')
    .slice(0, 48)
    .replace(/-+$/g, '');
  const base = slug && slug !== RESERVED_NAMESPACE ? slug : 'pdf';
  return `${base}-xxxxxx.okrapdf.dev`;
}

/** HTTP status of an RTK Query failure, including an unparseable body's original status. */
export function publishHttpStatus(err: unknown): number | undefined {
  const failure = err as { status?: unknown; originalStatus?: unknown } | undefined;
  const status = failure?.status === 'PARSING_ERROR' ? failure.originalStatus : failure?.status;
  return typeof status === 'number' && Number.isInteger(status) && status >= 100 && status <= 599
    ? status
    : undefined;
}

/** Body codes the API returns on the publish path (http-auth.ts, host-api-routes.ts). */
const ALLOWED_BODY_CODES = [
  // auth
  'invalid_token',
  'missing_credentials',
  'delegated_user_mismatch',
  'invalid_api_key',
  'api_key_auth_unavailable',
  'login_required',
  // access
  'invalid_access_mode',
  'invalid_access_disabled',
  'invalid_access_unlisted',
  'invalid_allow_download',
  'invalid_do_index',
  'invalid_expired_url',
  'invalid_expires_at',
  'invalid_password',
  // store
  'db_unavailable',
  'deployment_conflict',
  'invalid_deployment',
  'public_id_conflict',
  'document_not_indexed',
] as const;

/** Codes derived from exact server or web-proxy error strings (worker/hosted-pdf-boundary.ts). */
const DERIVED_CODES = [
  'pdf_type_rejected',
  'pdf_missing',
  'multipart_invalid',
  'proxy_bearer_missing',
  'account_unverified',
  'proxy_origin_forbidden',
  'namespace_taken',
  'namespace_empty',
  'namespace_format',
  'namespace_reserved',
  'proxy_upstream_unavailable',
  'service_unconfigured',
  'file_too_large',
] as const;

export const PUBLISH_SERVER_CODES: readonly string[] = [...ALLOWED_BODY_CODES, ...DERIVED_CODES];

const ALLOWED_BODY_CODE_SET: ReadonlySet<string> = new Set(ALLOWED_BODY_CODES);
const INVALID_NAMESPACE_REASONS: ReadonlySet<string> = new Set([
  'empty',
  'format',
  'reserved',
  'taken',
]);

const DERIVED_BY_ERROR: Record<number, Record<string, (typeof DERIVED_CODES)[number]>> = {
  400: {
    'Upload must be a PDF file': 'pdf_type_rejected',
    'Missing PDF file': 'pdf_missing',
    'Expected multipart/form-data with a PDF file': 'multipart_invalid',
  },
  401: {
    'Bearer authentication required': 'proxy_bearer_missing',
    'Authentication required': 'account_unverified',
  },
  403: { 'Cross-origin request forbidden': 'proxy_origin_forbidden' },
  409: { 'Namespace already taken': 'namespace_taken' },
  502: { 'Hosted PDF service unavailable': 'proxy_upstream_unavailable' },
};

function serverDerivedCode(
  status: number,
  error: string | undefined,
  code: string | undefined,
): string | undefined {
  if (status === 413) return 'file_too_large';
  if (error === undefined) return undefined;
  const exact = DERIVED_BY_ERROR[status]?.[error];
  if (exact) return exact;
  if (
    status === 422 &&
    error === 'Invalid namespace' &&
    code &&
    INVALID_NAMESPACE_REASONS.has(code)
  ) {
    return `namespace_${code}`;
  }
  if (status === 503 && error.endsWith(' not configured')) return 'service_unconfigured';
  return undefined;
}

/**
 * Enum-only server diagnosis for telemetry. Returns a value from
 * PUBLISH_SERVER_CODES or undefined — never the response's free text.
 */
export function publishServerCode(err: unknown): string | undefined {
  const data = (err as { data?: unknown } | undefined)?.data;
  const body =
    typeof data === 'object' && data !== null ? (data as { code?: unknown; error?: unknown }) : {};
  const code = typeof body.code === 'string' ? body.code : undefined;
  if (code && ALLOWED_BODY_CODE_SET.has(code)) return code;
  const status = publishHttpStatus(err);
  if (status === undefined) return undefined;
  const error = typeof body.error === 'string' ? body.error : undefined;
  const derived = serverDerivedCode(status, error, code);
  return derived && PUBLISH_SERVER_CODES.includes(derived) ? derived : undefined;
}

/**
 * True when the public publisher runs inside another page's frame on the
 * marketing host (F10). The Clerk modal and the publish request must not
 * start there; the runner offers its own tab instead. The comparison is
 * identity-only, so a cross-origin parent never throws, and any error reads
 * as not framed. The app host is never blocked.
 */
export function isFramedOnMarketingHost(
  win?: Pick<Window, 'self' | 'top'>,
  appHost?: boolean,
): boolean {
  try {
    if (appHost ?? isAppHost()) return false;
    const target = win ?? window;
    return target.self !== target.top;
  } catch {
    return false;
  }
}
